import { helpers } from '../utils/helpers';
import { apiCall, API_CONFIG, buildApiUrl } from './api';
import { authAPI } from './auth-api';
import { IS_ALFAJR } from '../constants/general';

const getMinOrderForDeliveryPayload = (couponData) => {
  if (IS_ALFAJR || couponData.discountType !== 'percentage_free_delivery') return 0;
  if (couponData.hasMinOrderForDelivery && couponData.minorderforDelivery) {
    return parseFloat(couponData.minorderforDelivery);
  }
  return 0;
};

export const couponAPI = {
  // Create new coupon
  createCoupon: async (couponData) => {
    try {
      const payload = {
        couponName: couponData.couponName,
        startDate: couponData.startDate || null,
        startTime: couponData.startTime || null,
        endDate: couponData.endDate || null,
        endTime: couponData.endTime || null,
        userType: couponData.userType,
        discountType: couponData.discountType,
        percentageValue: (couponData.discountType === 'percentage' || couponData.discountType === 'percentage_free_delivery') ? parseFloat(couponData.percentageValue) : 0,
        maxDiscountAmount: couponData.maxDiscountAmount ? parseFloat(couponData.maxDiscountAmount) : 0,
        minOrderAmount: couponData.minOrderAmount ? parseFloat(couponData.minOrderAmount) : 0,
        minorderforDelivery: getMinOrderForDeliveryPayload(couponData),
        usageType: couponData.usageType,
        usageLimit: couponData.usageType === 'limited' ? parseInt(couponData.usageLimit) : 0,
        applicationType: couponData.applicationType,
        applicationMode: couponData.applicationMode,
        selectedCategories: couponData.applicationType === 'selected_categories'
          ? couponData.selectedCategories || []
          : [],
        selectedProducts: couponData.applicationType === 'selected_products'
          ? couponData.selectedProducts || []
          : [],
        firstOrderOnlyCount: couponData.isFirstXOrdersOnly && couponData.firstOrderOnlyCount ? parseInt(couponData.firstOrderOnlyCount) : 0,
        sendNotifications: couponData.sendNotifications !== undefined ? couponData.sendNotifications : false,
        onlyForThisStore: couponData.onlyForThisStore !== undefined ? couponData.onlyForThisStore : true,
        isPublic: couponData.isPublic !== undefined ? couponData.isPublic : true,
        redeemType: couponData.redeemType || 'direct',
      };

      if (couponData.userType === 'single' && couponData.phoneNumber) {
        payload.phoneNumber = couponData.phoneNumber;
      } else if (couponData.userType === 'bulk' && couponData.csvFile) {
        const phoneNumbers = await helpers.extractPhoneNumbersFromCSV(couponData.csvFile);
        payload.phoneNumbers = phoneNumbers;
      }

      const response = await apiCall(API_CONFIG.ENDPOINTS.COUPONS_ADD, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          msg: response.msg || 'Coupon created successfully'
        };
      } else {
        return {
          success: false,
          error: response.msg || 'Failed to create coupon',
          errorCode: response.errorCode
        };
      }
    } catch (error) {
      console.error('Error creating coupon:', error);
      return {
        success: false,
        error: 'Error creating coupon: ' + error.message
      };
    }
  },

  // Get all coupons
  getAllCoupons: async (page = 1, limit = 20, search = '', userFilter = '', createdBy = '', expiryFilter = '', createdAt = '', status = '', couponName = '', couponId = '', creditType = '') => {
    try {
      const queryParams = new URLSearchParams();

      if (page) queryParams.append('page', page);
      if (limit) queryParams.append('limit', limit);
      if (search) queryParams.append('search', search);
      if (userFilter) queryParams.append('userFilter', userFilter);
      if (createdBy) queryParams.append('createdBy', createdBy);
      if (expiryFilter) queryParams.append('expiryFilter', expiryFilter);
      if (createdAt) queryParams.append('createdAt', createdAt);
      if (status) queryParams.append('status', status);
      if (couponName) queryParams.append('couponName', couponName);
      if (couponId) queryParams.append('couponId', couponId);
      if (creditType) queryParams.append('creditType', creditType);

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.COUPONS_LIST}?${queryParams.toString()}`,
        { method: 'GET' }
      );

      if (response.success) {
        return {
          success: true,
          data: response.data,
          pagination: response.pagination,
          stats: response.stats || null,
          msg: response.msg || 'Coupons fetched successfully'
        };
      } else {
        return {
          success: false,
          error: response.msg || 'Failed to fetch coupons'
        };
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      return {
        success: false,
        error: 'Error fetching coupons: ' + error.message
      };
    }
  },

  updateCoupon: async (payload) => {
    try {
      const { couponId } = payload;

      const response = await apiCall(`${API_CONFIG.ENDPOINTS.COUPONS_UPDATE}/${couponId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          msg: response.msg || 'Coupon updated successfully'
        };
      } else {
        return {
          success: false,
          error: response.msg || 'Failed to update coupon',
          errorCode: response.errorCode
        };
      }
    } catch (error) {
      console.error('Error updating coupon:', error);
      return {
        success: false,
        error: 'Error updating coupon: ' + error.message
      };
    }
  },

  deleteCoupon: async (couponId) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.COUPONS_DELETE}/${couponId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          msg: response.msg || 'Coupon deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.msg || 'Failed to delete coupon',
          errorCode: response.errorCode
        };
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      return {
        success: false,
        error: 'Error deleting coupon: ' + error.message
      };
    }
  },

  // Get coupon analytics
  getCouponAnalytics: async (couponId) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.COUPONS_ANALYTICS}/${couponId}`, {
        method: 'GET'
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          msg: response.msg || 'Analytics fetched successfully'
        };
      }

      return {
        success: false,
        error: response.msg || 'Failed to fetch analytics'
      };
    } catch (error) {
      console.error('Error fetching coupon analytics:', error);
      return {
        success: false,
        error: 'Error fetching analytics: ' + error.message
      };
    }
  },

  getNotificationDetails: async (couponId) => {
    try {
      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.COUPONS_NOTIFICATION_QUE}/${couponId}/notification-details`,
        { method: 'GET' }
      );

      if (response.success) {
        return {
          success: true,
          data: response.data,
          msg: response.msg || 'Notification details fetched successfully',
        };
      }

      return {
        success: false,
        error: response.msg || 'Failed to fetch notification details',
        errorCode: response.errorCode,
      };
    } catch (error) {
      console.error('Error fetching coupon notification details:', error);
      return {
        success: false,
        error: 'Error fetching notification details: ' + error.message,
      };
    }
  },

  createNotificationQue: async (couponId, { title, bodyText, image = null }) => {
    try {
      const token = authAPI.getToken();
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const formData = new FormData();
      formData.append('title', title);
      formData.append('bodyText', bodyText);

      if (image instanceof File) {
        formData.append('image', image);
      }

      const response = await fetch(
        buildApiUrl(`${API_CONFIG.ENDPOINTS.COUPONS_NOTIFICATION_QUE}/${couponId}/notification-que`),
        {
          method: 'POST',
          headers,
          body: formData,
        }
      );

      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          data: data.data,
          msg: data.msg || 'Notification que created successfully',
        };
      }

      return {
        success: false,
        error: data.msg || 'Failed to create notification que',
        errorCode: data.errorCode,
      };
    } catch (error) {
      console.error('Error creating coupon notification que:', error);
      return {
        success: false,
        error: 'Error creating notification que: ' + error.message,
      };
    }
  },

  createWhatsappQue: async (couponId, queData) => {
    try {
      const token = authAPI.getToken();
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const formData = new FormData();
      formData.append('templateName', queData.templateName);
      formData.append('headerType', queData.headerType);

      if (queData.headerText) {
        formData.append('headerText', queData.headerText);
      }

      if (queData.headerImage instanceof File) {
        formData.append('headerImage', queData.headerImage);
      }

      formData.append('variables', JSON.stringify(queData.variables || []));
      formData.append('buttons', JSON.stringify(queData.buttons || []));

      const response = await fetch(
        buildApiUrl(`${API_CONFIG.ENDPOINTS.COUPONS_NOTIFICATION_QUE}/${couponId}/whatsapp-que`),
        {
          method: 'POST',
          headers,
          body: formData,
        }
      );

      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          data: data.data,
          msg: data.msg || 'WhatsApp que created successfully',
        };
      }

      return {
        success: false,
        error: data.msg || 'Failed to create WhatsApp que',
        errorCode: data.errorCode,
      };
    } catch (error) {
      console.error('Error creating coupon WhatsApp que:', error);
      return {
        success: false,
        error: 'Error creating WhatsApp que: ' + error.message,
      };
    }
  },
};
